import baseApi from './RootBaseApi';
import {
  LegalAcceptanceEvent,
  LegalAcceptanceSummary,
  LegalComplianceQueryParams,
  LegalComplianceResponse,
} from '@/types/legal-compliance.types';

const emptyComplianceResponse = (): LegalComplianceResponse => ({
  stats: {
    total_acceptance_events: 0,
    unique_subjects_accepted: 0,
    unique_users_accepted: 0,
    current_terms_version: 0,
    current_privacy_version: 0,
    breakdown_by_subject_type: {},
    breakdown_by_context: {},
    breakdown_by_document_type: {},
    breakdown_by_document_version: {},
  },
  list: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    total_pages: 0,
  },
});

const unwrapComplianceResponse = (
  response: unknown
): LegalComplianceResponse => {
  if (!response || typeof response !== 'object') {
    return emptyComplianceResponse();
  }

  const wrapped = response as {
    data?: LegalComplianceResponse;
    stats?: LegalComplianceResponse['stats'];
    list?: LegalComplianceResponse['list'];
  };

  if (wrapped.data?.stats && wrapped.data?.list) {
    return wrapped.data;
  }

  if (wrapped.stats && wrapped.list) {
    return {
      stats: wrapped.stats,
      list: wrapped.list,
    };
  }

  return emptyComplianceResponse();
};

const buildQueryString = (params?: LegalComplianceQueryParams): string => {
  if (!params) return '';
  const search = new URLSearchParams();
  if (params.startDate) search.append('startDate', params.startDate);
  if (params.endDate) search.append('endDate', params.endDate);
  if (params.documentType) search.append('documentType', params.documentType);
  if (params.subjectType) search.append('subjectType', params.subjectType);
  if (params.context) search.append('context', params.context);
  if (params.language) search.append('language', params.language);
  if (params.documentVersion !== undefined) {
    search.append('documentVersion', String(params.documentVersion));
  }
  if (params.currentVersionOnly) {
    search.append('currentVersionOnly', 'true');
  }
  if (params.page) search.append('page', String(params.page));
  if (params.limit) search.append('limit', String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export const LegalComplianceService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLegalCompliance: builder.query<
      LegalComplianceResponse,
      LegalComplianceQueryParams | void
    >({
      query: (params) => ({
        url: `/legal-acceptance/compliance${buildQueryString(params ?? undefined)}`,
        method: 'GET',
      }),
      transformResponse: unwrapComplianceResponse,
      providesTags: ['LegalCompliance'],
    }),
    getSubjectLegalAcceptance: builder.query<
      { latest: LegalAcceptanceSummary; history: LegalAcceptanceEvent[] },
      { subjectType: string; subjectId: string }
    >({
      query: ({ subjectType, subjectId }) => ({
        url: `/legal-acceptance/subjects/${subjectType}/${subjectId}`,
        method: 'GET',
      }),
      transformResponse: (
        response: unknown
      ): { latest: LegalAcceptanceSummary; history: LegalAcceptanceEvent[] } => {
        const raw = response as {
          data?: { latest: LegalAcceptanceSummary; history: LegalAcceptanceEvent[] };
          latest?: LegalAcceptanceSummary;
          history?: LegalAcceptanceEvent[];
        };
        if (raw.data?.latest !== undefined) {
          return raw.data;
        }
        return {
          latest: raw.latest ?? { terms: null, privacy: null, history_count: 0 },
          history: raw.history ?? [],
        };
      },
    }),
    exportLegalComplianceCsv: builder.query<string, LegalComplianceQueryParams | void>({
      query: (params) => ({
        url: `/legal-acceptance/compliance/export${buildQueryString(params ?? undefined)}`,
        method: 'GET',
        responseType: 'text',
      }),
    }),
  }),
});

export const {
  useGetLegalComplianceQuery,
  useLazyExportLegalComplianceCsvQuery,
  useGetSubjectLegalAcceptanceQuery,
} = LegalComplianceService;
